import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";

const LABELS = {
  denomination: "Dénomination",
  legal_form: "Forme juridique",
  tax_regime: "Régime fiscal",
  siren: "SIREN",
  siret: "SIRET",
  share_capital: "Capital social",
  address: "Adresse du siège",
  city: "Ville",
  postal_code: "Code postal",
  creation_date: "Date de création",
  fiscal_year_end: "Clôture d'exercice",
  activity: "Activité",
  corporate_purpose: "Objet social",
  legal_representative: "Représentant légal",
  contact_email: "E-mail de contact",
  main_bank: "Banque principale",
  accountant: "Comptable",
  shareholders: "Participations",
  documents: "Documents",
  "reference_data.activity": "Activité (réf.)",
  "reference_data.ownership_pct": "% détenu",
  "reference_data.estimated_value": "Valorisation estimée",
};

export default function CompanyCompletenessTab({ company }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/companies/${company.id}/completeness`).then(({ data }) => setData(data));
  }, [company.id]);

  if (!data) return <div className="text-muted-foreground text-sm">Chargement…</div>;

  const pct = data.pct;
  const tone = pct >= 80 ? "emerald" : pct >= 50 ? "amber" : "red";
  const barColor = tone === "emerald" ? "bg-emerald-600" : tone === "amber" ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="space-y-6">
      <div className="card-elevated p-6 lg:p-8">
        <div className="text-overline">Score de complétude</div>
        <div className="mt-2 flex items-baseline gap-3">
          <span className="font-serif text-5xl">{pct} %</span>
          <span className="text-sm text-muted-foreground">
            {pct === 100 ? "Dossier complet." : "Dossier en cours de constitution."}
          </span>
        </div>
        <div className="mt-4 h-3 w-full rounded-full bg-secondary overflow-hidden">
          <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <CheckRow ok={data.checks.fiscal_info_ok} label="Régime fiscal & TVA renseignés" />
          <CheckRow ok={data.checks.bank_ok} label="Banque principale renseignée" />
          <CheckRow ok={data.checks.shareholders_ok} label="Au moins une participation" />
        </div>
      </div>

      {data.missing.length > 0 && (
        <div className="card-elevated p-6">
          <div className="text-overline mb-3">À compléter</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {data.missing.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500" />
                <span>{LABELS[f] || f}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            La création de l'entité n'est pas bloquée par un dossier incomplet — vous pouvez compléter à votre rythme.
          </p>
        </div>
      )}
    </div>
  );
}

function CheckRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl border p-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50" : "border-border bg-secondary/40"}`}>
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
      <span className={ok ? "text-emerald-800" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}
